import { LocalShipping } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { grey } from '@mui/material/colors';
import { sendApplicationFormInitialValues } from 'modules/sendCreditApplication/constants';
import { sendApplicationFormSchema } from 'modules/sendCreditApplication/schema';
import { SendApplicationFormValues } from 'modules/sendCreditApplication/types';
import { FormSection } from 'modules/sendCreditApplication/ui/sendApplicationForm/FormSection';
import { ContactDetailsSection } from 'modules/sendCreditApplication/ui/sendApplicationForm/ContactDetailsSection';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Box, Stack, Select, MenuItem, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { useMutation } from '@apollo/client';
import { MutationResponse } from 'global';
import { sendCreditApplicationApi } from 'modules/sendCreditApplication/api';
import { SendOcaLinkVariables } from 'modules/sendCreditApplication/api/types';
import { formatFormValuesToSendOcaLinkPayload } from 'modules/sendCreditApplication/lib';
import { useEquipmentForm } from '../../modules/equipment/hooks/useEquipmentForm';
import { useNotifications } from 'modules/notification';
import { SendOcaLinkSendMethods } from '@trnsact/trnsact-shared-types';
import { lowercaseFirstLetter } from 'formatters';

export const EquipmentPageSendEquipmentFlyer = ({ equipmentId }: { equipmentId: string }) => {
  const form = useForm<SendApplicationFormValues>({
    resolver: zodResolver(sendApplicationFormSchema),
    defaultValues: sendApplicationFormInitialValues,
  });

  const { form: equipmentForm, isLoading: isEquipmentFormLoading } = useEquipmentForm({ id: equipmentId });

  const [sendOcaLink, { loading: isSendOcaLinkLoading }] = useMutation<MutationResponse<string>, SendOcaLinkVariables>(
    sendCreditApplicationApi.sendOcaLink
  );

  const handleSendOcaLink = async (formValues: SendApplicationFormValues) => {
    const input = formatFormValuesToSendOcaLinkPayload(formValues);

    const equipmentFormValues = equipmentForm.getValues();

    if (equipmentFormValues.commonInfo.inventoryItem.inventoryId) {
      input.flyerMeta.listOfInventory = [equipmentFormValues.commonInfo.inventoryItem.inventoryId];
    }
    input.isPromoteFlyer = true;

    const { data } = await sendOcaLink({ variables: { input } });

    if (!data) {
      showNotification('Failed to send flyer', { type: 'error' });
      return null;
    }

    showNotification('Flyer sent successfully', { type: 'success' });

    return data?.result?.entity ?? null;
  };

  const { showNotification } = useNotifications();

  return (
    <Stack gap="2rem" sx={{ borderRadius: '0.5rem', border: `1px solid ${grey[300]}` }}>
      <FormProvider {...form}>
        <FormSection isExpanded={false} isExpandable title="Send Equipment Flyer" icon={<LocalShipping />}>
          <Box sx={{ p: 2 }}>
            <ContactDetailsSection />

            <Controller
              name="sendMethod"
              control={form.control}
              render={({ field }) => (
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{
                    mt: 2,
                    width: { xs: '100%', md: 'calc((100% - 2rem) / 3)' }, // matches 1 column of repeat(3, 1fr) with gap: 1rem
                  }}
                >
                  <InputLabel id="send-method-select-label">Send by Text and/or Email</InputLabel>
                  <Select
                    labelId="send-method-select-label"
                    id="send-method-select"
                    value={field.value}
                    onChange={field.onChange}
                    input={<OutlinedInput label="Send by Text and/or Email" />}
                  >
                    {Object.keys(SendOcaLinkSendMethods).map(key => (
                      <MenuItem key={key} value={lowercaseFirstLetter(key)}>
                        {key.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" onClick={form.handleSubmit(handleSendOcaLink)} loading={isSendOcaLinkLoading}>
                Send Flyer
              </Button>
            </Box>
          </Box>
        </FormSection>
      </FormProvider>
    </Stack>
  );
};
